"use client";

import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-8 items-center">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-5 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  setIsOpen
}: {
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-6">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-8 w-32 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <div className="flex flex-col gap-4 p-6 bg-background border-b border-border shadow-lg">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} onClick={() => setIsOpen(false)} className="text-lg font-medium text-foreground hover:text-primary transition-colors py-2" data-index={i}>
          {item.label}
        </Link>)}
      <div className="pt-4 border-t border-border mt-2">
        <Button className="w-full bg-gradient-to-r from-primary to-chart-2 text-primary-foreground shadow-md" size="lg">
          Request a Quote
        </Button>
      </div>
    </div>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="main-navigation" className={`sticky top-0 z-50 w-full transition-all duration-300 ease-in-out h-[80px] flex items-center ${isScrolled ? "bg-background/80 backdrop-blur-[12px] border-b border-border shadow-sm" : "bg-transparent border-b border-border/10"}`}>
      <div className="container mx-auto px-4 md:px-8 flex items-center justify-between w-full h-full">
        {}
        <div className="flex-shrink-0 flex items-center mx-auto md:mx-0">
          <Link to="/" className="flex items-center">
            <img src="https://cliffhanger.websiteshop.au/wp-content/uploads/2026/03/CHP-Logo-Master.png" alt="Cliff Hanger Plumbing" className="h-[60px] w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="17">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden md:flex flex-shrink-0 items-center">
          <Button className="bg-gradient-to-r from-primary to-chart-2 hover:from-primary/90 hover:to-chart-2/90 text-primary-foreground shadow-[0_4px_14px_0_var(--color-primary)_/_30%] hover:shadow-[0_6px_20px_var(--color-primary)_/_23%] hover:-translate-y-0.5 transition-all duration-200 border-0 rounded-full px-6">
            Request a Quote
          </Button>
        </div>

        {}
        <div className="md:hidden flex items-center">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" className="text-foreground">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="absolute top-[80px] left-0 w-full bg-background md:hidden animate-in slide-in-from-top-2 duration-200">
          <MenuProvider menu_id="17">
            <MobileMenuConsumer setIsOpen={setIsMobileMenuOpen} />
          </MenuProvider>
        </div>}
    </header>;
}